import { Reveal, useFeedbackModal } from "@components/ui";
import { type ReactNode } from "react";
import { Link } from "react-router-dom";

/* ─────────────────────────────────────────────────────────
   Inline SVG Icons
   ───────────────────────────────────────────────────────── */

function AlertTriangleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="8" y2="15" />
      <line x1="11" y1="15" x2="15" y2="15" />
    </svg>
  );
}

function FileTextIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function GaugeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
      <path d="M16.24 7.76l-1.42 1.42" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function WrenchIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:translate-x-1">
      <path d="M5 12H19M19 12L13 6M19 12L13 18" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────
   Types & Data
   ───────────────────────────────────────────────────────── */

interface TopicCard {
  title: string;
  subtitle: string;
  link?: string;
  modalType?: string;
  icon: ReactNode;
  color: string;
  bgTint: string;
}

const topicCards: TopicCard[] = [
  {
    title: "Report a Gas Leak",
    subtitle: "Emergency Hotline",
    modalType: "Emergency / Maintenance",
    icon: <AlertTriangleIcon />,
    color: "#dc2626",
    bgTint: "rgba(220, 38, 38, 0.1)",
  },
  {
    title: "Pay My Bill",
    subtitle: "Online Payment Portal",
    link: "/services/pay-bill",
    icon: <CreditCardIcon />,
    color: "#122d63",
    bgTint: "rgba(18, 45, 99, 0.1)",
  },
  {
    title: "Understand My Bill",
    subtitle: "Billing Explained",
    link: "/services/billing-payment",
    icon: <FileTextIcon />,
    color: "#16a34a",
    bgTint: "rgba(22, 163, 74, 0.1)",
  },
  {
    title: "Submit Meter Reading",
    subtitle: "Submit Reading Page",
    link: "/services/gas-services",
    icon: <GaugeIcon />,
    color: "#7c3aed",
    bgTint: "rgba(124, 58, 237, 0.1)",
  },
  {
    title: "Moving Out",
    subtitle: "Move-Out Form",
    modalType: "General Feedback",
    icon: <TruckIcon />,
    color: "#ea580c",
    bgTint: "rgba(234, 88, 12, 0.1)",
  },
  {
    title: "Book Maintenance",
    subtitle: "Maintenance Request Form",
    modalType: "Emergency / Maintenance",
    icon: <WrenchIcon />,
    color: "#1e3a5f",
    bgTint: "rgba(30, 58, 95, 0.1)",
  },
];

/* ─────────────────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────────────────── */

const cardClassName =
  "group flex items-center h-full py-5 md:py-[30px] px-5 md:px-[31px] gap-4 md:gap-6 rounded-[16px] bg-white border border-[rgba(98,99,108,0.25)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] transition-all duration-300 w-full text-left";

function CardContent({ card }: { card: TopicCard }) {
  return (
    <>
      {/* Icon */}
      <div
        className="w-14 h-14 rounded-[12px] flex items-center justify-center shrink-0"
        style={{ backgroundColor: card.bgTint, color: card.color }}
      >
        {card.icon}
      </div>

      {/* Text */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <h3 className="text-[22px] md:text-[28px] font-semibold text-black tracking-[-0.02em] leading-tight">
          {card.title}
        </h3>
        <p className="text-[16px] md:text-[20px] text-[#62636c] truncate">
          {card.subtitle}
        </p>
      </div>

      {/* Arrow */}
      <div className="shrink-0 text-[#62636c] group-hover:text-[#122d63] transition-colors duration-300">
        <ArrowRightIcon />
      </div>
    </>
  );
}

export default function PopularTopicsSection() {
  const { openModal } = useFeedbackModal();

  return (
    <section className="w-full bg-[#f1f1f1] py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto font-albert-sans">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 mb-10 lg:mb-14 text-center">
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] font-semibold text-[#122d63] tracking-[-0.04em] leading-tight">
              Popular Topics
            </h2>
            <p className="text-[16px] md:text-[20px] text-[#62636c] max-w-[600px]">
              Quick access to frequently requested services.
            </p>
          </div>
        </Reveal>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {topicCards.map((card, index) => (
            <Reveal key={index} direction="up" delay={80 + index * 60} className="h-full">
              {card.modalType ? (
                <button
                  onClick={() => openModal(card.modalType)}
                  className={cardClassName}
                >
                  <CardContent card={card} />
                </button>
              ) : (
                <Link
                  to={card.link!}
                  className={cardClassName}
                >
                  <CardContent card={card} />
                </Link>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
